if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
        autoWidth: 'carousel--auto-width',
        fadePartial: 'carousel--fade-partial',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.tabletLargeItems = parseInt(this.dataset.tabletLargeItems);
      this.cardWidth = parseInt(this.dataset.cardWidth) || 0;
      this.fadePartial = this.dataset.fadePartial === 'true';

      this.sliderInstance = false;

      if (!this.enableSlider) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.init();
      document.addEventListener(
        'matchMobile',
        () => {
          this.init();
        },
        { signal }
      );
      document.addEventListener(
        'unmatchMobile',
        () => {
          this.init();
        },
        { signal }
      );
      if (this.cardWidth) {
        window.addEventListener('resize', FoxTheme.utils.debounce(this.refreshLoop.bind(this), 200), { signal });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (typeof this.sliderInstance === 'object') this.destroySlider();
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

      const sliderOptions = {
        slidesPerView: 2,
        spaceBetween: columnGap.mobile,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.tabletItems),
            spaceBetween: columnGap.tablet
          },
          1024: {
            slidesPerView: parseInt(this.tabletLargeItems),
            spaceBetween: columnGap.tabletLarge
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop
          },
        },
        loop: true,
        threshold: 2,
      };

      // A card width (section setting) replaces the columns: the CSS sizes each slide.
      if (this.cardWidth) {
        Object.values(sliderOptions.breakpoints).forEach((params) => (params.slidesPerView = 'auto'));
      }

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        if (this.cardWidth) this.classList.add(this.classes.autoWidth);
        if (this.fadePartial) this.classList.add(this.classes.fadePartial);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        if (this.cardWidth) {
          const width = window.innerWidth;
          const breakpoint = Object.keys(sliderOptions.breakpoints)
            .filter((min) => width >= Number(min))
            .pop();
          const gap = (sliderOptions.breakpoints[breakpoint] || sliderOptions).spaceBetween;
          Object.assign(sliderOptions, this.loopOptions(gap));
        }
        // Any kit object turns on the F5 kit's keyboard focus sync; without the new options the call stays as
        // before. The slider bleeds past the section (section-testimonials.scss), so a card cut by the screen
        // edge is measured against the section's visible box (`clip`), not the container.
        const kit = this.cardWidth || this.fadePartial ? { fadePartial: this.fadePartial, clip: this.section } : null;
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions, null, kit);
        this.sliderInstance.init();

        // The slider is rebuilt on breakpoint and loop changes: its listeners go with it (destroySlider).
        this.sliderController = new AbortController();
        const { signal } = this.sliderController;
        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener(
            'focusin',
            () => {
              if (!this.sliderInstance) return;
              const slide = element.closest('.swiper-slide');
              this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
            },
            { signal }
          );
        });

        if (Shopify.designMode) {
          document.addEventListener(
            'shopify:block:select',
            (e) => {
              if (e.detail.sectionId != this.sectionId || !this.sliderInstance) return;
              let { target } = e;
              const index = Number(target.dataset.index);

              this.sliderInstance.slider.slideToLoop(index);
            },
            { signal }
          );
        }
      }
    }

    // With a card width the columns no longer bound the row. A loop period (one copy of every card) shorter
    // than the visible box plus one card puts a review and its clone in view together, so that row doesn't
    // loop: it rewinds, and when it is longer than the visible box its end is pulled back by the part of the
    // container the section clips, so the last card can come fully into view (Swiper would otherwise count
    // the cards in the bleed as shown). A row that fits stays put: Swiper locks it and hides the arrows.
    loopOptions(gap) {
      const slides = Array.from(this.sliderWrapper.children).filter(
        (slide) => !slide.classList.contains('swiper-slide-duplicate')
      );
      const widths = slides.map((slide) => slide.getBoundingClientRect().width);
      const period = widths.reduce((sum, width) => sum + width + gap, 0);
      const box = this.section.getBoundingClientRect();
      const rect = this.getBoundingClientRect();
      const visible = Math.min(box.right, rect.right) - Math.max(box.left, rect.left);
      if (period >= visible + Math.max(...widths) - 2) return { loop: true, rewind: false, slidesOffsetAfter: 0 };
      // Swiper ends the row at max(row, container) + slidesOffsetAfter; the end should be the visible edge.
      const row = period - gap;
      return {
        loop: false,
        rewind: true,
        slidesOffsetAfter: Math.max(0, Math.round(Math.min(rect.width, row) - visible)),
      };
    }

    // The card width scales with the screen, so the loop decision is remade on resize.
    refreshLoop() {
      if (typeof this.sliderInstance !== 'object') return;
      const slider = this.sliderInstance.slider;
      const options = this.loopOptions(slider.params.spaceBetween);
      if (options.loop !== slider.params.loop) {
        this.destroySlider();
        this.initSlider();
      } else if (options.slidesOffsetAfter !== slider.params.slidesOffsetAfter) {
        slider.params.slidesOffsetAfter = options.slidesOffsetAfter;
        slider.update();
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper, this.classes.autoWidth, this.classes.fadePartial);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        // The kit's destroy runs Swiper's destroy(true, true) (= its defaults) and releases the focus sync.
        this.sliderInstance.destroy();
        this.sliderInstance = false;
        this.sliderController.abort();
      }
    }
  }
  customElements.define('testimonials-component', Testimonials);
}
